import styles from "./AppInput.module.scss";

import {
  ChangeEvent,
  KeyboardEvent,
  Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { EyeClosedSvg } from "../../svgs/EyeClosedSvg";
import { EyeOpenSvg } from "../../svgs/EyeOpenSvg";

interface AppInputBaseProps {
  id?: string;
  type?: "text" | "email" | "password" | "textarea";
  autoComplete?: string;
  ariaLabel?: string;
  describedBy?: string;
  invalid?: boolean;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  autoCapitalize?: string;
  /**
   * Ref to the underlying input element. Not applied when type="textarea".
   */
  inputRef?: Ref<HTMLInputElement>;
  error?: string;
  maxLength?: number;
  showCharacterCounter?: boolean;
  characterCounterFormat?: (count: number, max: number) => string;
  disabled?: boolean;
  className?: string;
  /**
   * When true, the textarea will automatically grow as the user types.
   * Only applies when type="textarea".
   */
  autoGrow?: boolean;
  /**
   * When false, maxLength is not enforced on the input element,
   * allowing users to type beyond the limit (useful for showing exceeded errors).
   * Defaults to true for backward compatibility.
   */
  enforceMaxLength?: boolean;
  /**
   * Data attributes to apply to the input/textarea element (e.g., { "data-1p-ignore": "" })
   */
  dataAttributes?: Record<string, string>;
  /**
   * Masks this field's value in PostHog Session Replay via `data-ph-mask`.
   * Defaults to `true` for password inputs so they are never recorded in plaintext.
   */
  maskInReplay?: boolean;
  /**
   * Visual size of the input/label. "large" uses 12px input padding and a medium-weight label.
   */
  size?: "default" | "large";
}

/**
 * When `showPasswordToggle` is enabled, `passwordToggleLabel` is required so the
 * eye button always has an accessible name. The discriminated union below makes
 * TypeScript enforce that pairing at the call site.
 */
type AppInputProps =
  | (AppInputBaseProps & {
      showPasswordToggle: true;
      passwordToggleLabel: { show: string; hide: string };
    })
  | (AppInputBaseProps & {
      showPasswordToggle?: false;
      passwordToggleLabel?: never;
    });

export const AppInput = ({
  id,
  type = "text",
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  autoCapitalize,
  inputRef,
  error,
  maxLength,
  showCharacterCounter = false,
  characterCounterFormat,
  disabled = false,
  className = "",
  autoGrow = false,
  enforceMaxLength = true,
  dataAttributes,
  maskInReplay,
  autoComplete,
  ariaLabel,
  describedBy,
  invalid,
  showPasswordToggle = false,
  passwordToggleLabel,
  size = "default",
}: AppInputProps) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorMessageId = `${inputId}-error`;
  const isTextarea = type === "textarea";
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const hasPasswordToggle = showPasswordToggle && type === "password";
  const effectiveType = hasPasswordToggle && isPasswordVisible ? "text" : type;

  const shouldMaskInReplay = maskInReplay ?? type === "password";
  const inputDataAttributes = shouldMaskInReplay
    ? { ...dataAttributes, "data-ph-mask": "" }
    : dataAttributes;

  useEffect(() => {
    if (autoGrow && isTextarea && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value, autoGrow, isTextarea]);

  const getCharacterCounterText = () => {
    if (!maxLength) return "";
    if (characterCounterFormat) {
      return characterCounterFormat(value.length, maxLength);
    }
    return `${value.length} of ${maxLength}`;
  };

  const isCharacterLimitExceeded = maxLength ? value.length > maxLength : false;

  const hasVisibleError =
    Boolean(error) && !(showCharacterCounter && isCharacterLimitExceeded);
  // The error joins aria-describedby so it stays part of the field's
  // accessible description after the one-shot role="alert" announcement.
  const composedDescribedBy =
    [describedBy, hasVisibleError ? errorMessageId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  const effectiveMaxLength = enforceMaxLength ? maxLength : undefined;

  const isLarge = size === "large";
  const inputSizeClass = isLarge ? styles.inputLarge : "";
  const labelSizeClass = isLarge ? styles.labelLarge : "";
  const fieldSizeClass = isLarge ? styles.fieldLarge : "";

  return (
    <div className={`${styles.field} ${fieldSizeClass} ${className}`}>
      {label && (
        <label
          className={`${styles.label} ${labelSizeClass}`}
          htmlFor={inputId}
        >
          {label}
        </label>
      )}
      {isTextarea ? (
        <textarea
          ref={textareaRef}
          id={inputId}
          className={`${styles.textarea} ${autoGrow ? styles.textareaAutoGrow : ""} ${error || isCharacterLimitExceeded ? styles.textareaError : ""}`}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          autoCapitalize={autoCapitalize}
          maxLength={effectiveMaxLength}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={composedDescribedBy}
          aria-invalid={invalid}
          {...inputDataAttributes}
        />
      ) : hasPasswordToggle ? (
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            id={inputId}
            type={effectiveType}
            className={`${styles.input} ${inputSizeClass} ${error || isCharacterLimitExceeded ? styles.inputError : ""} ${styles.inputWithTrailingButton}`}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            autoCapitalize={autoCapitalize}
            maxLength={effectiveMaxLength}
            disabled={disabled}
            autoComplete={autoComplete}
            aria-label={ariaLabel}
            aria-describedby={composedDescribedBy}
            aria-invalid={invalid}
            {...inputDataAttributes}
          />
          <button
            type="button"
            className={styles.passwordToggle}
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            disabled={disabled}
            aria-label={
              isPasswordVisible
                ? passwordToggleLabel?.hide
                : passwordToggleLabel?.show
            }
            aria-pressed={isPasswordVisible}
          >
            {isPasswordVisible ? <EyeClosedSvg /> : <EyeOpenSvg />}
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          id={inputId}
          type={effectiveType}
          className={`${styles.input} ${inputSizeClass} ${error || isCharacterLimitExceeded ? styles.inputError : ""}`}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          autoCapitalize={autoCapitalize}
          maxLength={effectiveMaxLength}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-label={ariaLabel}
          aria-describedby={composedDescribedBy}
          aria-invalid={invalid}
          {...inputDataAttributes}
        />
      )}
      {showCharacterCounter && maxLength && (
        <div
          className={`${styles.characterCounter} ${isCharacterLimitExceeded ? styles.characterCounterError : ""}`}
        >
          {getCharacterCounterText()}
        </div>
      )}
      {hasVisibleError && (
        <div className={styles.errorMessage} id={errorMessageId} role="alert">
          {error}
        </div>
      )}
    </div>
  );
};
